import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import AssignDriver from '@/components/order/AssignDriver';
import OrderStatusUpdate from '@/components/order/OrderStatus';
import Card from '@/components/UI/Card';
import DateRangePicker from '@/components/UI/DateRange';
import { useListQueryParams } from '@/hooks/useListQueryParams';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig } from '@/interfaces/common.interface';
import {
  IOrderResponse,
  IOrderStates,
  Order,
} from '@/interfaces/order.interface';
import {
  formatDateTime,
  formatDeliveryRange,
  GetFilterUrl,
  getPaymentMethodMessage,
  orderFilterOption,
  OrderStatus,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

const Orders: React.FC = () => {
  const { canView, canEdit } = usePermissions();
  const navigate = useNavigate();

  //  URL-driven filters
  const { params, setParams } = useListQueryParams();

  const { page, limit, search, orderStatus, paymentStatus, type, isJunk } =
    params;

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: params.startDate ? new Date(params.startDate) : undefined,

      endDate: params.endDate ? new Date(params.endDate) : undefined,

      key: 'selection',
    },
  ]);

  const [state, setState] = useState<IOrderStates>({
    order: [],
    pageInfo: {
      total: 10,
      totalPages: 1,
      limit,
      page,
    },
    showPaymentModel: false,
    isLoading: false,
  });

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChangeState = (payload: Partial<IOrderStates>) => {
    setState((prev) => ({ ...prev, ...payload }));
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.order);

  const fetchOrders = async () => {
    try {
      handleChangeState({ isLoading: true });

      const paramsForApi: Record<string, any> = {
        page,
        limit,
        search,
        status: orderStatus,
        type,
      };

      if (!isJunk) {
        paramsForApi.paymentStatus = paymentStatus;
      }

      if (isJunk) {
        paramsForApi.isJunk = true;
      }

      if (params.startDate && params.endDate) {
        paramsForApi.startDate = formatDateTime(
          new Date(params.startDate),
          'YYMMDD',
        );

        paramsForApi.endDate = formatDateTime(
          new Date(params.endDate),
          'YYMMDD',
        );
      }

      const query = GetFilterUrl(paramsForApi);
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}?${query}`;

      const res = await callApi<IOrderResponse>(PATH, { method: 'GET' });

      handleChangeState({
        order: res.data,
        pageInfo: res.pageInfo,
        isLoading: false,
      });
    } catch {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [
    page,
    limit,
    search,
    orderStatus,
    paymentStatus,
    type,
    isJunk,
    params.startDate,
    params.endDate,
  ]);

  const updatePaymentStatus = async () => {
    if (!selectedOrderId) return;

    try {
      handleChangeState({ isLoading: true });

      await callApi(
        `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/update-status`,
        {
          method: 'PATCH',
          data: { id: selectedOrderId, paymentStatus: 'PAID' },
        },
      );

      showToastNotification('success', 'Payment status updated to paid.');
      fetchOrders();
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ isLoading: false, showPaymentModel: false });
      setSelectedOrderId(null);
    }
  };

  // 🔹 Filter change handler
  const handleFilterChange = (filter: string) => {
    // Payment based filters
    if (filter === 'PAID' || filter === 'UNPAID') {
      setParams({
        paymentStatus: filter,
        isJunk: '',
        orderStatus: '',
        page: 1,
      });
      return;
    }

    // Junk orders → REMOVE paymentStatus
    if (filter === 'isJunk') {
      setParams({
        isJunk: 'true',
        paymentStatus: '',
        orderStatus: '',
        page: 1,
      });
      return;
    }

    // Normal order status filter
    setParams({
      orderStatus: filter,
      paymentStatus: 'PAID',
      isJunk: '',
      page: 1,
    });
  };

  const activeFilter = useMemo(() => {
    if (isJunk === 'true') return 'isJunk';
    if (paymentStatus && paymentStatus !== 'PAID') return paymentStatus;
    if (orderStatus) return orderStatus;
    return '';
  }, [isJunk, paymentStatus, orderStatus]);

  const handleDateRangeChange: React.Dispatch<React.SetStateAction<Range[]>> = (
    value,
  ) => {
    const next = typeof value === 'function' ? value(dateRange) : value;
    setDateRange(next);
    const startDate = next[0]?.startDate?.toISOString();
    const endDate = next[0]?.endDate?.toISOString();

    if (startDate !== params.startDate || endDate !== params.endDate) {
      setParams({
        startDate,
        endDate,
        page: 1,
      });
    }
  };

  const orderColumns: TableColumn<Order>[] = [
    {
      key: 'orderId',
      header: 'Order ID',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.orderId}
        </span>
      ),
    },
    {
      key: 'dateTime',
      header: 'Date/Time',
      render: (order) => {
        const deliveredInfo = order.previousStatus?.find(
          (v) => v.status === OrderStatus.DELIVERED,
        );
        return (
          <span
            className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}
          >
            {deliveredInfo?.timestamp
              ? formatDateTime(deliveredInfo.timestamp, 'YYMMMDDHH')
              : formatDeliveryRange(order.formattedDeliveryTime)}
          </span>
        );
      },
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.personalInfo.firstName} {order.personalInfo.lastName}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total Amount BD',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {toFixedThree(order.grandTotal)}
        </span>
      ),
    },
    {
      key: 'orderStatus',
      header: 'Order Status',
      render: (order) => (
        <OrderStatusUpdate order={order} onSucess={fetchOrders} />
      ),
    },
    {
      key: 'paymentType',
      header: 'Payment Type',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {getPaymentMethodMessage(
            order.paymentMethod?.type,
            order.paymentStatus,
          ) ?? 'N/A'}
        </span>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.orderItems?.length ?? 0}
        </span>
      ),
    },
    {
      key: 'paymentStatus',
      header: 'Payment Status',
      render: (item) => (
        <StatusDropdown
          value={item.paymentStatus}
          options={
            item.paymentStatus !== 'PAID'
              ? [{ value: 'PAID', label: 'Paid' }]
              : []
          }
          onChange={() => {
            handleChangeState({ showPaymentModel: true });
            setSelectedOrderId(item.id);
          }}
          canEditVal="order"
        />
      ),
    },
  ];

  const orderActionConfig: Record<string, ActionConfig<Order>> = {
    view: {
      text: 'View',
      onClick: (order) => navigate(`${RoutesLink.orderDetail}/${order.id}`),
      permission: () => canView('order'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(orderActionConfig)
        .filter((a) => a.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit],
  );

  const filterOptions = [
    { value: 'customers', label: 'Customers' },
    { value: 'employee', label: 'Employees' },
  ];

  return (
    <Layout>
      <PageHeading text="Orders" />

      <Card>
        <div className=" flex flex-col lg:flex-row w-full mb-4 items-start flex-wrap lg:items-center justify-between gap-3 ">
          <CommonFilter
            activeFilter={activeFilter}
            filters={orderFilterOption}
            inputPlaceHolder="Search for Orders..."
            searchValue={search}
            onSearch={(text) => setParams({ search: text })}
            onChange={handleFilterChange}
            filterOptions={{
              values: filterOptions,
              onChange: (value) => setParams({ type: value }),
            }}
          />

          <div className="flex items-center gap-3">
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={handleDateRangeChange}
            />
            {state.order.length > 0 && (
              <ExportOption
                APIPath={`${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/download-report`}
                dateRange={dateRange[0]}
                status={orderStatus}
              />
            )}
          </div>
        </div>

        <CommonTable
          columns={orderColumns}
          data={state.order}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Order found"
          loading={state.isLoading}
          actions={actions}
        />

        <Pagination
          paginationData={state.pageInfo}
          onPageChange={(p) => setParams({ page: p })}
          onPageSizeChange={(l) => setParams({ limit: l })}
        />

        {isModalOpen && selectedOrderId && (
          <AssignDriver
            id={selectedOrderId}
            setIsOpen={setIsModalOpen}
            onSuccess={() => {
              fetchOrders();
              setSelectedOrderId(null);
            }}
          />
        )}

        {state.showPaymentModel && (
          <ConfirmationModel
            modelType="update"
            show
            type="Paid"
            subTitle="Are you sure you want to update payment status to"
            onClose={() => {
              handleChangeState({ showPaymentModel: false });
              setSelectedOrderId(null);
            }}
            onConfirm={updatePaymentStatus}
          />
        )}
      </Card>
    </Layout>
  );
};

export default Orders;
